import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useEffect } from 'react';
import Footer from './Footer';

interface InstallationServiceProps {
  t: any;
  isRTL: boolean;
}

const InstallationServiceContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="hex" width="20" height="20" patternUnits="userSpaceOnUse"><polygon points="10,2 18,6 18,14 10,18 2,14 2,6" fill="none" stroke="%23dc2626" stroke-width="0.5" opacity="0.1"/></pattern></defs><rect width="100" height="100" fill="url(%23hex)"/></svg>');
    animation: rotate 30s linear infinite;
    z-index: 1;
  }
  
  @keyframes rotate {
    0% { transform: translate(-50%, -50%) rotate(0deg); }
    100% { transform: translate(-50%, -50%) rotate(360deg); }
  }
`;

const FloatingElements = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  overflow: hidden;
  z-index: 1;
`;

const FloatingShape = styled.div`
  position: absolute;
  background: linear-gradient(135deg, rgba(220, 38, 38, 0.1) 0%, rgba(185, 28, 28, 0.1) 100%);
  border-radius: 50%;
  
  &:nth-child(1) {
    width: 300px;
    height: 300px;
    top: 10%;
    left: 5%;
    animation: float 6s ease-in-out infinite;
  }
  
  &:nth-child(2) {
    width: 200px;
    height: 200px;
    top: 60%;
    right: 10%;
    animation: float 8s ease-in-out infinite reverse;
  }
  
  &:nth-child(3) {
    width: 150px;
    height: 150px;
    bottom: 20%;
    left: 15%;
    animation: float 7s ease-in-out infinite;
  }
  
  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-20px); }
  }
`;

const Container = styled.div<{ $isRTL: boolean }>`
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
  position: relative;
  z-index: 2;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const HeroSection = styled.section`
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  margin-bottom: 4rem;
  border-radius: 20px;
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('/images/tch.jpg') center/cover;
    opacity: 0.2;
    z-index: 1;
  }
`;

const HeroContent = styled.div<{ $isRTL: boolean }>`
  position: relative;
  z-index: 2;
  text-align: center;
  color: white;
  max-width: 800px;
  padding: 0 2rem;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const HeroTitle = styled.h1<{ $isRTL: boolean }>`
  font-size: 4rem;
  font-weight: 800;
  color: white;
  margin: 0 0 2rem 0;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p<{ $isRTL: boolean }>`
  font-size: 1.3rem;
  color: #e2e8f0;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.7;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  font-weight: 400;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const Section = styled.div`
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(220, 38, 38, 0.1);
  margin-bottom: 4rem;
  overflow: hidden;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  }
`;

const SectionContent = styled.div`
  padding: 4rem;
  
  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ContentGridReversed = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
  
  .text-section {
    order: 1;
  }
  
  .image-section {
    order: 2;
  }
  
  @media (min-width: 769px) {
    .text-section {
      order: 2;
    }
    
    .image-section {
      order: 1;
    }
  }
`;

const ImageSection = styled.div`
  position: relative;
`;

const ServiceImage = styled.img`
  width: 100%;
  height: 600px;
  object-fit: cover;
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: scale(1.02);
  }
  
  @media (max-width: 768px) {
    height: 350px;
  }
`;

const TextSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  
  &.text-section {
    gap: 2.5rem;
  }
`;

const SectionTitle = styled.h2<{ $isRTL: boolean }>`
  font-size: 3rem;
  color: #0f172a;
  margin-bottom: 2rem;
  margin-top: 2rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
  
  background: linear-gradient(135deg, #0f172a 0%, #dc2626 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  /* Make titles span full page width and center horizontally */
  width: 100%;
  text-align: center;
  line-height: 1.1;
  word-spacing: 0.1em;
  max-width: none;
  overflow-wrap: break-word;
  hyphens: auto;
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
    line-height: 1.2;
  }
`;

const ServiceDescription = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Paragraph = styled.p<{ $isRTL: boolean }>`
  font-size: 1.1rem;
  line-height: 1.8;
  color: #64748b;
  margin-bottom: 1.5rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
  
  &:last-child {
    margin-bottom: 0;
  }
  
  strong {
    color: #dc2626;
    font-weight: 600;
  }
`;

const LogoSection = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2rem;
  padding: 2rem;
  background: rgba(248, 249, 250, 0.8);
  border-radius: 16px;
  border: 1px solid rgba(220, 38, 38, 0.1);
`;

const TechyidLogo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.8rem;
`;

const LogoImage = styled.img`
  height: 80px;
  width: auto;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1));
`;

const Slogan = styled.div<{ $isRTL: boolean }>`
  font-size: 1.1rem;
  color: #dc2626;
  font-weight: 600;
  letter-spacing: 2px;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
`;

const SolutionsContainer = styled.div<{ $isRTL: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
  margin-top: 2rem;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const SolutionItem = styled.div<{ $isRTL: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  padding: 1.5rem;
  background: rgba(248, 249, 250, 0.8);
  border-radius: 12px;
  border-left: 4px solid #dc2626;
  transition: all 0.3s ease;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  
  &:hover {
    background: rgba(248, 249, 250, 1);
    transform: translateX(${props => props.$isRTL ? '-4px' : '4px'});
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
    gap: 1rem;
    
    &:hover {
      transform: none;
    }
  }
`;

const SolutionIcon = styled.div`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  margin-top: 4px;
  
  &::before {
    content: '❖';
    color: #dc2626;
    font-size: 1.2rem;
    font-weight: bold;
  }
`;

const SolutionContent = styled.div<{ $isRTL: boolean }>`
  flex: 1;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const SolutionTitle = styled.h3<{ $isRTL: boolean }>`
  font-size: 1.2rem;
  color: #0f172a;
  margin-bottom: 0.75rem;
  font-weight: 600;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
`;

const SolutionDescription = styled.p<{ $isRTL: boolean }>`
  font-size: 1rem;
  line-height: 1.7;
  color: #64748b;
  margin: 0;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
`;

const InstallationService: React.FC<InstallationServiceProps> = ({ t, isRTL }) => {
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  const expertiseAreas = [
    {
      title: "Construction & Installation",
      description: "un acteur de premier plan dans les secteurs de l'énergie et des télécommunications, une entreprise de référence pour des services de construction et d'installation exceptionnels."
    },
    {
      title: "Commissioning (Mise en service)",
      description: "Management & Consultancy"
    },
    {
      title: "Electrical Testing & Commissioning",
      description: "will find the right reliable systems and services solution for your electrical power needs"
    },
    {
      title: "Instrumentation",
      description: "propose des services d'essai et de mise en service d'instruments"
    },
    {
      title: "Telecom & System Integration",
      description: "ELV & Telecom Systems Integration business line"
    },
    {
      title: "HVAC Systems & Services",
      description: "expert and specialist Engineers & Technicians will carry out the complete HVAC System procurement, and installation"
    },
    {
      title: "Process & Mechanical Systems",
      description: "expert and specialist Engineers & Technicians"
    },
    {
      title: "Preventive maintenance services",
      description: "Comprehensive maintenance solutions to ensure optimal system performance and longevity"
    }
  ];

  return (
    <>
    <InstallationServiceContainer>
        <FloatingElements>
          <FloatingShape />
          <FloatingShape />
          <FloatingShape />
        </FloatingElements>
        
        <Container $isRTL={isRTL}>
          {/* First Section - Image on Left */}
          <Section>
            <SectionContent>
              <ContentGrid>
                <ImageSection>
                  <ServiceImage
                    src="/images/tch.jpg"
                    alt="TCH Installation Service"
                    loading="lazy"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = '/images/logo.png';
                    }}
                  />
                </ImageSection>

                <TextSection>
                  <SectionTitle $isRTL={isRTL}>{t.installationServiceTitle}</SectionTitle>
                  
                  <ServiceDescription>
                    <Paragraph $isRTL={isRTL}>
                      <strong>{t.techyid}</strong> {t.installationServiceIntro}
                    </Paragraph>
                    
                    <Paragraph $isRTL={isRTL}>
                      {t.installationServiceKeyActor}
                    </Paragraph>
                    
                    <Paragraph $isRTL={isRTL}>
                      • {t.installationServiceExpertise}<br/>
                      • {t.installationServiceInterfaces}<br/>
                      • {t.installationServiceTesting}<br/>
                      • {t.installationServiceIntegration}
                    </Paragraph>
                    
                    <Paragraph $isRTL={isRTL}>
                      {t.installationServiceQuality}
                    </Paragraph>
                    
                    <Paragraph $isRTL={isRTL}>
                      <strong>{t.techyid}</strong> {t.installationServiceComprehensive}
                    </Paragraph>
                  </ServiceDescription>
                </TextSection>
              </ContentGrid>
            </SectionContent>
          </Section>

          {/* Second Section - Text on Left, Image on Right */}
          <Section>
            <SectionContent>
              <ContentGrid>
                <TextSection>
                  <SectionTitle $isRTL={isRTL}>{t.installationServiceSubtitle}</SectionTitle>
                  
                  <ServiceDescription>
                    <Paragraph $isRTL={isRTL}>
                      <strong>{t.techyid}</strong> {t.installationServiceRange}
                    </Paragraph>
                    
                    <Paragraph $isRTL={isRTL}>
                      • <strong>{t.constructionInstallation}</strong> : {t.constructionInstallationDesc}
                    </Paragraph>
                    
                    <Paragraph $isRTL={isRTL}>
                      • <strong>{t.commissioning}</strong> : {t.commissioningDesc}
                    </Paragraph>
                    
                    <Paragraph $isRTL={isRTL}>
                      • <strong>{t.electricalTesting}</strong> : {t.electricalTestingDesc}
                    </Paragraph>
                    
                    <Paragraph $isRTL={isRTL}>
                      • <strong>{t.instrumentation}</strong> : {t.instrumentationDesc}
                    </Paragraph>
                    
                    <Paragraph $isRTL={isRTL}>
                      • <strong>{t.telecomIntegration}</strong> : {t.telecomIntegrationDesc}
                    </Paragraph>
                  </ServiceDescription>
                </TextSection>

                <ImageSection>
                  <ServiceImage
                    src="/images/tch2.png"
                    alt="TCH2 Installation Service"
                    loading="lazy"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = '/images/logo.png';
                    }}
                  />
                </ImageSection>
              </ContentGrid>
            </SectionContent>
          </Section>
      </Container>
      </InstallationServiceContainer>
      <Footer t={t} isRTL={isRTL} />
    </>
  );
};

export default InstallationService;
